import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CheckBox, Divider, Flex, Grid, Spacer, Text } from '@oliasoft-open-source/react-ui-library';
import translations from 'client/internationalization/translation-map.json';
import { HierarchyLevelType } from 'common/interfaces/hierarchy-entities';
import { hierarchyLevelTypeName } from 'client/views/explore/hierarchy/hierarchy.utils';
import { TSyncTreeNode } from './sync-designs-tree.utils';

export type TSyncTreeSelection = {
  expandAll: boolean,
  checkedIds: Set<string>,
  syncStatuses: { [designid: string]: boolean },
  onChange: (designIds: string[], checked: boolean) => void,
};

type TSyncDesignsTreeItemProps = TSyncTreeSelection & {
  node: TSyncTreeNode,
};

export const SyncDesignsTreeItem = memo(({ node, ...selection }: TSyncDesignsTreeItemProps) => {
  const { expandAll, checkedIds, syncStatuses, onChange } = selection;
  const { t } = useTranslation();
  const [isToggledOpen, setIsToggledOpen] = useState(false);
  const isOpen = expandAll || isToggledOpen;

  const isGroup = node.level !== HierarchyLevelType.Design;
  const selectableIds = node.designIds.filter((designid) => !syncStatuses[designid]);
  const checkedCount = selectableIds.filter((designid) => checkedIds.has(designid)).length;
  const isChecked = selectableIds.length > 0 && checkedCount === selectableIds.length;
  const isAllSyncing = selectableIds.length === 0;

  const designsCountLabel = node.designIds.length === 1
    ? t(translations.design_oneDesign)
    : t(translations.design_manyDesigns, { count: node.designIds.length });

  return (
    <>
      <Flex alignItems="center" gap>
        {isGroup && !expandAll
          ? <Button small basic round icon={isOpen ? 'chevron down' : 'chevron right'} onClick={() => setIsToggledOpen(!isOpen)} />
          : <Spacer width="var(--size-sm)" />}
          <CheckBox
            noMargin
            checked={isChecked}
            indeterminate={checkedCount > 0 && !isChecked}
            disabled={isAllSyncing}
            onChange={() => onChange(selectableIds, !isChecked)}
            label={<Text bold>{node.name}</Text>}
          />
        <Text muted>{hierarchyLevelTypeName[node.level]}</Text>
        <Spacer flex />
        {isGroup && <Text muted>{designsCountLabel}</Text>}
        {!isGroup && syncStatuses[node.id] && <Text small muted>{t(translations.design_syncing)}</Text>}
        {!syncStatuses[node.id] && node.design?.last_synced_out_at && (
          <Text success small>{t(translations.design_synced)}</Text>
        )}
      </Flex>
      <Divider margin={`var(--padding-xs)`} />
      {isGroup && isOpen && (
        <Grid columns="var(--padding-xs) 1fr">
          <Spacer />
          <Flex direction="column">
            {node.children.map((child) => (
              <SyncDesignsTreeItem key={child.id} node={child} {...selection} />
            ))}
          </Flex>
        </Grid>
      )}
    </>
  );
});
